import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Input,
  Table,
  FormGroup,
  Label,
} from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../Utils";
import { getData, postData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";

interface CustomerItem {
  Id?: number;
  id?: number;
  OutletOrgDataId?: number;
  outlet_org_data_id?: number;
  OutletOrgId?: number;
  OutletName?: string;
  outlet_name?: string;
  OutlettypeName?: string;
  outlet_type_name?: string;
  Classification?: string;
  outlet_classification?: string;
  PCode?: string;
  p_code?: string;
  VFQ?: number;
  done_VFQ?: number;
  VF?: string;
  LastVisited?: string;
  last_visited?: string;
  Address?: string;
  address?: string;
  BeatId?: number;
  beat_id?: number;
  BeatName?: string;
  beat_name?: string;
  itownid?: number;
  Itownid?: number;
  itown_id?: number;
  Itown_id?: number;
}

interface PlannedItem {
  TpId?: number;
  tp_id?: number;
  OutletName?: string;
  outlet_name?: string;
  Agendname?: string;
  Agendacontroltype?: string;
  agendacontrol_type?: string;
  OutlettypeName?: string;
  outlet_type_name?: string;
  BeatName?: string;
  beat_name?: string;
  TownName?: string;
  town_name?: string;
  VF?: string;
  VisitFq?: number;
}

const MtpDayPlanner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const serverUrl = localStorage.getItem("serverUrl") || "";

  const {
    date: stateDate,
    dayId: stateDayId,
    mtpId: stateMtpId,
    mtpStatus: stateMtpStatus,
  } = location.state || {};

  const [currentDate, setCurrentDate] = useState<Date>(
    stateDate ? new Date(stateDate) : new Date()
  );
  const [dayId, setDayId] = useState<number | string>(stateDayId || 0);
  const [mtpId, setMtpId] = useState<number | string>(stateMtpId || 0);
  const [status, setStatus] = useState<string>(stateMtpStatus || "DRAFT");

  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);

  // Activity Dropdown (FW, NCA, JW)
  const [activity, setActivity] = useState<string>("FW");

  // Territory Dropdown
  const [territories, setTerritories] = useState<Array<{ label: string; value: number }>>([]);
  const [selectedTerritory, setSelectedTerritory] = useState<number | string>("");

  // Patch / Beat Dropdown (for FW)
  const [patches, setPatches] = useState<Array<{ label: string; value: number }>>([]);
  const [rawPatches, setRawPatches] = useState<any[]>([]);
  const [selectedPatch, setSelectedPatch] = useState<number | string>("");

  // Town Dropdown (for NCA)
  const [towns, setTowns] = useState<Array<{ label: string; value: number }>>([]);
  const [selectedTown, setSelectedTown] = useState<number | string>("");

  // NCA Agenda Subtypes & General Agenda States
  const [ncaAgendas, setNcaAgendas] = useState<Array<{ label: string; value: number }>>([]);
  const [selectedNcaAgenda, setSelectedNcaAgenda] = useState<number | string>("");
  const [selectedAgenda, setSelectedAgenda] = useState<number | string>("");

  // Customer List States (Left Panel)
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const [availableCustomers, setAvailableCustomers] = useState<CustomerItem[]>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);

  // Planned List States (Right Panel)
  const [plannedCustomers, setPlannedCustomers] = useState<PlannedItem[]>([]);

  const formattedDateStr = moment(currentDate).format("YYYY-MM-DD");
  const formattedDisplayDate = moment(currentDate).format("dddd, DD MMMM YYYY");

  // Fetch Agenda Types by Activity
  const fetchAgendas = async (activityType: string, date: Date) => {
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const formattedDate = moment(date).format("YYYY-MM-DD");
      const urlAgenda = `${serverBaseUrl}${API.getMtpAgendas}`;
      const resAgenda = await getData(urlAgenda, true, {
        agenda_type: activityType,
        date: formattedDate,
      });

      let agendaList: any[] = [];
      if (resAgenda?.status === 200 && Array.isArray(resAgenda?.data?.data?.AgendaTypes)) {
        agendaList = resAgenda.data.data.AgendaTypes;
      } else if (resAgenda?.status === 200 && Array.isArray(resAgenda?.data?.AgendaTypes)) {
        agendaList = resAgenda.data.AgendaTypes;
      }

      if (agendaList.length > 0) {
        const firstId = agendaList[0].Agendaid || agendaList[0].agenda_id || agendaList[0].id || 0;
        if (activityType === "NCA") {
          const options = agendaList.map((item: any) => ({
            label: item.Agendname || item.agenda_name,
            value: item.Agendaid || item.agenda_id,
          }));
          setNcaAgendas(options);
          setSelectedNcaAgenda(firstId);
        } else {
          setSelectedAgenda(firstId);
        }
        return firstId;
      }
      return 0;
    } catch (err) {
      console.error("Error fetching agendas:", err);
      return 0;
    }
  };

  // 1. Fetch Territory List based on Activity and Month
  const fetchTerritories = async (currAct: string, currDate: Date) => {
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const moye = moment(currDate).format("MM-YYYY");
      const url = `${serverBaseUrl}${API.getTerritoriesArray}`;
      const params: any = currAct === "JW" ? { show_vacant: 0 } : { month: moye };

      const res = await getData(url, true, params);

      if (res?.status === 200 && res?.data?.data) {
        const dt = res.data.data;
        const options = Object.keys(dt).map((key) => ({
          label: dt[key],
          value: parseInt(key, 10),
        }));
        setTerritories(options);
        if (options.length > 0) {
          const firstTerrId = options[0].value;
          setSelectedTerritory(firstTerrId);
          if (currAct === "FW") {
            fetchPatches(firstTerrId);
          } else if (currAct === "JW") {
            fetchCustomers("JW", firstTerrId, "", currDate);
          }
        } else {
          setSelectedTerritory("");
          setPatches([]);
          setRawPatches([]);
          setAvailableCustomers([]);
        }
      } else {
        setTerritories([]);
        setSelectedTerritory("");
        setPatches([]);
        setRawPatches([]);
        setAvailableCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching territories:", err);
      setTerritories([]);
    }
  };

  // 2. Fetch Patches / Beats for Selected Territory
  const fetchPatches = async (terrId: number | string) => {
    if (!terrId) {
      setPatches([]);
      setRawPatches([]);
      setSelectedPatch("");
      setAvailableCustomers([]);
      return;
    }
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const url = `${serverBaseUrl}${API.getBeats}`;
      const res = await getData(url, true, { territory_id: terrId });

      if (res?.status === 200 && Array.isArray(res?.data?.data)) {
        setRawPatches(res.data.data);
        const options = res.data.data.map((item: any) => ({
          label: `${item.GeoTowns?.Stownname || "N/A"} / ${item.BeatName || item.beat_name}`,
          value: item.BeatId || item.beat_id,
        }));
        setPatches(options);
        if (options.length > 0) {
          const firstPatchId = options[0].value;
          setSelectedPatch(firstPatchId);
          fetchCustomers("FW", terrId, firstPatchId, currentDate);
        } else {
          setSelectedPatch("");
          setAvailableCustomers([]);
        }
      } else {
        setPatches([]);
        setRawPatches([]);
        setSelectedPatch("");
        setAvailableCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching patches:", err);
      setPatches([]);
      setRawPatches([]);
    }
  };

  // 3. Fetch Towns & NCA Agenda Subtypes (for NCA)
  const fetchNcaDetails = async (currDate: Date) => {
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const formattedDate = moment(currDate).format("YYYY-MM-DD");

      // Fetch NCA Agendas
      const urlAgenda = `${serverBaseUrl}${API.getMtpAgendas}`;
      const resAgenda = await getData(urlAgenda, true, {
        agenda_type: "NCA",
        date: formattedDate,
      });

      if (resAgenda?.status === 200 && Array.isArray(resAgenda?.data?.data?.AgendaTypes)) {
        const options = resAgenda.data.data.AgendaTypes.map((item: any) => ({
          label: item.Agendname,
          value: item.Agendaid,
        }));
        setNcaAgendas(options);
        if (options.length > 0) setSelectedNcaAgenda(options[0].value);
      } else if (resAgenda?.status === 200 && Array.isArray(resAgenda?.data?.AgendaTypes)) {
        const options = resAgenda.data.AgendaTypes.map((item: any) => ({
          label: item.Agendname,
          value: item.Agendaid,
        }));
        setNcaAgendas(options);
        if (options.length > 0) setSelectedNcaAgenda(options[0].value);
      }

      // Fetch Towns
      const urlTown = `${serverBaseUrl}${API.towns}`;
      const resTown = await getData(urlTown, true, {});
      if (resTown?.status === 200 && Array.isArray(resTown?.data?.data)) {
        const options = resTown.data.data.map((t: any) => ({
          label: t.Stownname || t.town_name || t.name,
          value: t.Itownid || t.town_id || t.id,
        }));
        setTowns(options);
        if (options.length > 0) setSelectedTown(options[0].value);
      }
    } catch (err) {
      console.error("Error fetching NCA details:", err);
    }
  };

  // 4. Fetch Customers by Beat / Territory
  const fetchCustomers = async (
    currAct: string = activity,
    terrId: number | string = selectedTerritory,
    patchId: number | string = selectedPatch,
    currDate: Date = currentDate
  ) => {
    if (currAct === "NCA") {
      setAvailableCustomers([]);
      return;
    }
    setTableLoading(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const formattedDate = moment(currDate).format("YYYY-MM-DD");
      let res: any;
      if (currAct === "JW" && terrId) {
        const url = `${serverBaseUrl}${API.getCustomerForJW}`;
        res = await getData(url, true, {
          territory_id: terrId,
          tp_date: formattedDate,
        });
      } else if (currAct === "FW" && patchId) {
        const url = `${serverBaseUrl}${API.getCustomersByBeat}`;
        res = await getData(url, true, {
          beat_id: patchId,
          tp_date: formattedDate,
        });
      }

      if (res?.status === 200 && Array.isArray(res?.data?.data)) {
        setAvailableCustomers(res.data.data);
      } else if (res?.status === 200 && Array.isArray(res?.data)) {
        setAvailableCustomers(res.data);
      } else {
        setAvailableCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setAvailableCustomers([]);
    } finally {
      setTableLoading(false);
    }
  };

  // 5. Fetch MTP & Day Context from getMTPListV2 if dayId or mtpId is missing/0
  const fetchMtpAndDayContext = async (targetDate: Date) => {
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const formattedDate = moment(targetDate).format("YYYY-MM-DD");
      const targetMoye = moment(targetDate).format("MM-YYYY");

      const urlV2 = `${serverBaseUrl}${API.getMTPListV2}`;
      const resV2 = await getData(urlV2, true, {});

      let list: any[] = [];
      if (resV2?.status === 200) {
        if (Array.isArray(resV2?.data?.data?.mtp)) list = resV2.data.data.mtp;
        else if (Array.isArray(resV2?.data?.mtp)) list = resV2.data.mtp;
        else if (Array.isArray(resV2?.data?.data)) list = resV2.data.data;
        else if (Array.isArray(resV2?.data)) list = resV2.data;
      }

      if (list.length === 0) return { resolvedMtpId: mtpId || 0, resolvedDayId: dayId || 0 };

      // Match target month
      const matchedMtp = list.find(
        (m) =>
          m.MtpMonthYear === targetMoye ||
          m.mtp_month_year === targetMoye ||
          m.month === targetMoye
      ) || list[0];

      const resolvedMtpId = matchedMtp?.MtpId || matchedMtp?.mtp_id || matchedMtp?.id || mtpId || 0;
      if (resolvedMtpId) setMtpId(resolvedMtpId);
      if (matchedMtp?.MtpStatus || matchedMtp?.mtp_status) {
        setStatus(matchedMtp.MtpStatus || matchedMtp.mtp_status);
      }

      // Match target day
      const daysArr: any[] = matchedMtp?.MtpDays || matchedMtp?.mtp_days || matchedMtp?.days || [];
      const matchedDay = daysArr.find((d) => {
        const dDate = d.MtpDayDate || d.mtp_day_date || d.date || "";
        const formattedDDate = dDate.includes("T") ? dDate.split("T")[0] : dDate.split(" ")[0];
        return formattedDDate === formattedDate;
      });

      const resolvedDayId = matchedDay?.MtpDayId || matchedDay?.mtp_day_id || matchedDay?.id || dayId || 0;
      if (resolvedDayId) setDayId(resolvedDayId);

      // Fetch Day Plan Details using resolvedDayId
      if (resolvedDayId) {
        fetchDayPlanDetailsById(resolvedDayId);
      }

      return { resolvedMtpId, resolvedDayId };
    } catch (err) {
      console.error("Error fetching MTP & Day context:", err);
      return { resolvedMtpId: mtpId || 0, resolvedDayId: dayId || 0 };
    }
  };

  // 6. Fetch Day Plan Details by dayId
  const fetchDayPlanDetailsById = async (targetDayId: number | string) => {
    if (!targetDayId) return;
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const url = `${serverBaseUrl}${API.getMtpDayById}`;
      const res = await getData(url, true, { mtp_day_id: targetDayId });

      if (res?.status === 200 && Array.isArray(res?.data?.data?.MtpDay)) {
        setPlannedCustomers(res.data.data.MtpDay);
      } else if (res?.status === 200 && Array.isArray(res?.data?.MtpDay)) {
        setPlannedCustomers(res.data.MtpDay);
      } else {
        setPlannedCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching day plan details:", err);
      setPlannedCustomers([]);
    }
  };

  // Initial & Date/DayId Change Effect
  useEffect(() => {
    fetchMtpAndDayContext(currentDate);
  }, [currentDate]);

  // Activity or Date Change Effect
  useEffect(() => {
    setSelectedCustomerIds([]);
    fetchAgendas(activity, currentDate);
    if (activity === "FW" || activity === "JW") {
      fetchTerritories(activity, currentDate);
    } else if (activity === "NCA") {
      fetchNcaDetails(currentDate);
    }
  }, [activity, currentDate]);

  const handleActivityChange = (newAct: string) => {
    setActivity(newAct);
  };

  const handleTerritoryChange = (terrId: number | string) => {
    setSelectedTerritory(terrId);
    if (activity === "FW") {
      fetchPatches(terrId);
    } else if (activity === "JW") {
      fetchCustomers("JW", terrId, "", currentDate);
    }
  };

  const handlePatchChange = (patchId: number | string) => {
    setSelectedPatch(patchId);
    fetchCustomers("FW", selectedTerritory, patchId, currentDate);
  };

  const handleDateChange = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = availableCustomers
        .map((c) => c.OutletOrgDataId || c.outlet_org_data_id || c.OutletOrgId || 0)
        .filter(Boolean);
      setSelectedCustomerIds(allIds);
    } else {
      setSelectedCustomerIds([]);
    }
  };

  const handleToggleCustomer = (id: number) => {
    setSelectedCustomerIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAddToPlan = async () => {
    if (activity !== "NCA" && selectedCustomerIds.length === 0) {
      toast.warning("Please select at least one customer to add to plan.");
      return;
    }

    setLoading(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      // Ensure non-zero mtpId and dayId
      let activeMtpId = mtpId;
      let activeDayId = dayId;
      if (!activeMtpId || !activeDayId || activeMtpId === 0 || activeDayId === 0) {
        const resolved = await fetchMtpAndDayContext(currentDate);
        activeMtpId = resolved.resolvedMtpId;
        activeDayId = resolved.resolvedDayId;
      }

      // Ensure active agenda ID
      let targetAgendaId = activity === "NCA" ? selectedNcaAgenda : selectedAgenda;
      if (!targetAgendaId || targetAgendaId === 0) {
        targetAgendaId = await fetchAgendas(activity, currentDate);
      }

      const url = `${serverBaseUrl}${API.addTourPlan}`;

      if (activity === "NCA") {
        const outletIdForNca = selectedCustomerIds.length > 0 ? selectedCustomerIds[0] : 0;
        const res = await postData(url, true, {
          mtp_id: activeMtpId || 0,
          mtp_day_id: activeDayId || 0,
          agendacontroltype: "NCA",
          beat_id: 0,
          itownid: selectedTown || 0,
          agenda_id: targetAgendaId || 0,
          outlet_org_data_id: outletIdForNca,
        });

        if (res.status === 200 || (res as any).statusCode === 200) {
          toast.success("NCA activity added to plan!");
          const dta = res?.data?.MtpDay || (res as any)?.data?.data?.MtpDay || res?.data?.data?.MtpDay;
          if (Array.isArray(dta)) {
            setPlannedCustomers(dta);
          }
        } else {
          toast.error((res as any).message || res.data?.message || "Failed to add NCA plan.");
        }
      } else {
        // FW or JW
        const patchObj = rawPatches.find(
          (p: any) => Number(p.BeatId || p.beat_id) === Number(selectedPatch)
        );
        const patchItownid =
          patchObj?.Itownid ??
          patchObj?.itownid ??
          patchObj?.GeoTowns?.Itownid ??
          patchObj?.GeoTowns?.itownid ??
          0;

        const results = await Promise.all(
          selectedCustomerIds.map((id) => {
            const cust = availableCustomers.find((c: any) => {
              const cId = c.OutletOrgDataId || c.outlet_org_data_id || c.OutletOrgId || c.Id || c.id;
              return Number(cId) === Number(id);
            });

            const custItownid =
              cust?.itownid ?? cust?.Itownid ?? cust?.itown_id ?? cust?.Itown_id ?? 0;

            const finalItownid = Number(custItownid) || Number(patchItownid) || 0;

            return postData(url, true, {
              mtp_id: activeMtpId || 0,
              mtp_day_id: activeDayId || 0,
              agendacontroltype: activity,
              beat_id: activity === "FW" ? selectedPatch || 0 : 0,
              itownid: finalItownid,
              agenda_id: targetAgendaId || 0,
              outlet_org_data_id: id,
            });
          })
        );
        toast.success("Customers added to plan successfully!");
        const lastRes: any = results[results.length - 1];
        const dta = lastRes?.data?.MtpDay || lastRes?.data?.data?.MtpDay;
        if (Array.isArray(dta)) {
          setPlannedCustomers(dta);
        }
      }

      setSelectedCustomerIds([]);
      if (activeDayId) fetchDayPlanDetailsById(activeDayId);
    } catch (err) {
      console.error("Error adding to plan:", err);
      toast.error("Failed to add customers to plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlannedItem = async (tpId: number) => {
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";

      const url = `${serverBaseUrl}${API.deleteTourPlan}`;
      const res = await getData(url, true, { TpId: tpId });

      if (res?.status === 200) {
        toast.success("Planned customer removed!");
        if (dayId) fetchDayPlanDetailsById(dayId);
      } else {
        toast.error("Failed to remove planned customer.");
      }
    } catch (err) {
      console.error("Error deleting tour plan item:", err);
    }
  };

  const filteredAvailable = availableCustomers.filter((item) => {
    const name = item.OutletName || item.outlet_name || "";
    return name.toLowerCase().includes(customerSearch.toLowerCase());
  });

  // Calculate planned type summary string (e.g. DOCTOR: 2 | PHARMACY: 1)
  const typeCounts: Record<string, number> = {};
  plannedCustomers.forEach((item) => {
    const type = item.OutlettypeName || item.outlet_type_name || "OTHER";
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });
  const summaryPill = Object.keys(typeCounts)
    .map((k) => `${k.toUpperCase()}: ${typeCounts[k]}`)
    .join(" | ");

  const showTerritoryDropdown = (activity === "FW" || activity === "JW") && territories.length > 1;

  return (
    <div className="page-body p-3">
      {/* Top Header */}
      <div className="d-flex align-items-center mb-3">
        <Button
          color="primary"
          className="btn-pill me-3 p-2 d-flex align-items-center justify-content-center"
          style={{ width: "36px", height: "36px", borderRadius: "50%" }}
          onClick={() => navigate(-1)}
        >
          <i className="icofont icofont-arrow-left" style={{ fontSize: "18px" }}></i>
        </Button>
        <div>
          <h4 className="mb-0 font-weight-bold text-dark">MTP Day Planner</h4>
          <span className="text-warning small font-weight-bold">
            Status : {status.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Date Navigator Banner */}
      <div className="d-flex justify-content-center align-items-center mb-4">
        <div className="d-flex align-items-center bg-light p-1 rounded border shadow-sm">
          <Button
            color="primary"
            size="sm"
            className="px-3 py-1"
            onClick={() => handleDateChange("prev")}
          >
            <i className="icofont icofont-simple-left" />
          </Button>
          <span className="mx-4 font-weight-bold text-dark" style={{ fontSize: "16px" }}>
            {formattedDisplayDate}
          </span>
          <Button
            color="primary"
            size="sm"
            className="px-3 py-1"
            onClick={() => handleDateChange("next")}
          >
            <i className="icofont icofont-simple-right" />
          </Button>
        </div>
      </div>

      {/* Cascading Dropdowns Row */}
      <Row className="mb-4">
        {/* 1. Activity Dropdown */}
        <Col md={showTerritoryDropdown ? 4 : 6} sm={12} className="mb-2">
          <FormGroup>
            <Label className="font-weight-bold text-dark small">Activity</Label>
            <Input
              type="select"
              value={activity}
              onChange={(e) => handleActivityChange(e.target.value)}
              className="bg-white border text-dark font-weight-bold shadow-sm"
              style={{ color: "#000000", backgroundColor: "#ffffff", fontWeight: "bold" }}
            >
              <option value="FW" style={{ color: "#000000", backgroundColor: "#ffffff" }}>FW</option>
              <option value="NCA" style={{ color: "#000000", backgroundColor: "#ffffff" }}>NCA</option>
              <option value="JW" style={{ color: "#000000", backgroundColor: "#ffffff" }}>JW</option>
            </Input>
          </FormGroup>
        </Col>

        {/* 2. Territory Dropdown (Shown only for FW or JW when user has > 1 territory) */}
        {showTerritoryDropdown && (
          <Col md={4} sm={12} className="mb-2">
            <FormGroup>
              <Label className="font-weight-bold text-dark small">Territory</Label>
              <Input
                type="select"
                value={selectedTerritory}
                onChange={(e) => handleTerritoryChange(e.target.value)}
                className="bg-white border text-dark font-weight-bold shadow-sm"
                style={{ color: "#000000", backgroundColor: "#ffffff", fontWeight: "bold" }}
              >
                {territories.map((t) => (
                  <option key={t.value} value={t.value} style={{ color: "#000000", backgroundColor: "#ffffff" }}>
                    {t.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        )}

        {/* 3. Town/Patch Dropdown (Shown only when activity is FW) */}
        {activity === "FW" && patches.length > 0 && (
          <Col md={showTerritoryDropdown ? 4 : 6} sm={12} className="mb-2">
            <FormGroup>
              <Label className="font-weight-bold text-dark small">Town / Patch</Label>
              <Input
                type="select"
                value={selectedPatch}
                onChange={(e) => handlePatchChange(e.target.value)}
                className="bg-white border text-dark font-weight-bold shadow-sm"
                style={{ color: "#000000", backgroundColor: "#ffffff", fontWeight: "bold" }}
              >
                {patches.map((p) => (
                  <option key={p.value} value={p.value} style={{ color: "#000000", backgroundColor: "#ffffff" }}>
                    {p.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        )}

        {/* 4. NCA Subtype & Town Dropdowns (Shown only when activity is NCA) */}
        {activity === "NCA" && (
          <>
            <Col md={3} sm={12} className="mb-2">
              <FormGroup>
                <Label className="font-weight-bold text-dark small">NCA Subtype</Label>
                <Input
                  type="select"
                  value={selectedNcaAgenda}
                  onChange={(e) => setSelectedNcaAgenda(e.target.value)}
                  className="bg-white border text-dark font-weight-bold shadow-sm"
                  style={{ color: "#000000", backgroundColor: "#ffffff", fontWeight: "bold" }}
                >
                  {ncaAgendas.map((a) => (
                    <option key={a.value} value={a.value} style={{ color: "#000000", backgroundColor: "#ffffff" }}>
                      {a.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3} sm={12} className="mb-2">
              <FormGroup>
                <Label className="font-weight-bold text-dark small">Town</Label>
                <Input
                  type="select"
                  value={selectedTown}
                  onChange={(e) => setSelectedTown(e.target.value)}
                  className="bg-white border text-dark font-weight-bold shadow-sm"
                  style={{ color: "#000000", backgroundColor: "#ffffff", fontWeight: "bold" }}
                >
                  {towns.map((t) => (
                    <option key={t.value} value={t.value} style={{ color: "#000000", backgroundColor: "#ffffff" }}>
                      {t.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </>
        )}
      </Row>

      {/* Main Split View: Left (Available Customers) & Right (Planned Customers) */}
      <Row className="mb-4">
        {/* Left Panel: Available Customers */}
        <Col md={6} sm={12} className="mb-3">
          <Card className="border shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white">
              <h6 className="mb-0 font-weight-bold text-dark">
                {activity === "NCA" ? "Select Customers (Optional for NCA)" : "Customers List"}
              </h6>
              <Button color="primary" size="sm" className="px-3">
                <i className="icofont icofont-filter me-1" /> Filter
              </Button>
            </div>

            <div className="p-3 border-bottom bg-light">
              <Input
                type="text"
                placeholder="Search Customer"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="bg-white border shadow-none"
              />
            </div>

            <CardBody className="p-0 table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
              {tableLoading ? (
                <div className="text-center py-4">
                  <Image
                    className="b-r-10 img-60"
                    src={dynamicImage("dashboard/loader.gif")}
                    alt="Loading..."
                  />
                </div>
              ) : filteredAvailable.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  {activity === "NCA" ? "No specific customers needed for NCA (Click 'Add to plan' directly)" : "No Customers Found"}
                </div>
              ) : (
                <Table hover responsive className="align-middle mb-0">
                  <thead className="bg-light text-muted small">
                    <tr>
                      <th style={{ width: "40px" }}>
                        <Input
                          type="checkbox"
                          onChange={handleToggleSelectAll}
                          checked={
                            filteredAvailable.length > 0 &&
                            selectedCustomerIds.length === filteredAvailable.length
                          }
                        />
                      </th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Classification</th>
                      <th>PCode</th>
                      <th>VF</th>
                      <th>Last Visited</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody className="small">
                    {filteredAvailable.map((item, idx) => {
                      const id = item.OutletOrgDataId || item.outlet_org_data_id || item.OutletOrgId || idx;
                      const isSelected = selectedCustomerIds.includes(id);

                      return (
                        <tr key={idx} className={isSelected ? "table-active" : ""}>
                          <td>
                            <Input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleCustomer(id)}
                            />
                          </td>
                          <td className="font-weight-bold text-primary">
                            {item.OutletName || item.outlet_name || "-"}
                          </td>
                          <td>{item.OutlettypeName || item.outlet_type_name || "-"}</td>
                          <td>{item.Classification || item.outlet_classification || "MBBS"}</td>
                          <td>{item.PCode || item.p_code || "-"}</td>
                          <td>{item.VF || `${item.done_VFQ || 0}/${item.VFQ || 2}`}</td>
                          <td>{item.LastVisited || item.last_visited || "-"}</td>
                          <td>{item.Address || item.address || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Right Panel: Visit Planned Customers List */}
        <Col md={6} sm={12} className="mb-3">
          <Card className="border shadow-sm h-100">
            <div className="p-3 border-bottom bg-white">
              <h6 className="mb-1 font-weight-bold text-dark">
                Visit planned customers list ({plannedCustomers.length})
              </h6>
              {summaryPill && (
                <div className="text-primary font-weight-bold small mt-2 py-1 px-2 bg-light rounded text-center">
                  {summaryPill}
                </div>
              )}
            </div>

            <CardBody className="p-0 table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
              {plannedCustomers.length === 0 ? (
                <div className="text-center text-muted py-5 small">
                  No Planned Customers for this Day
                </div>
              ) : (
                <Table hover responsive className="align-middle mb-0">
                  <thead className="bg-light text-muted small">
                    <tr>
                      <th>Customer/Agenda</th>
                      <th>Type</th>
                      <th>Day Type</th>
                      <th>Beat / Town</th>
                      <th>VF</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody className="small">
                    {plannedCustomers.map((pItem, pIdx) => {
                      const tpId = pItem.TpId || pItem.tp_id || 0;
                      return (
                        <tr key={pIdx}>
                          <td className="font-weight-bold text-dark">
                            {pItem.OutletName || pItem.outlet_name || pItem.Agendname || "-"}
                          </td>
                          <td>{pItem.OutlettypeName || pItem.outlet_type_name || "-"}</td>
                          <td>{pItem.Agendacontroltype || pItem.agendacontrol_type || activity}</td>
                          <td>{pItem.BeatName || pItem.beat_name || pItem.TownName || "-"}</td>
                          <td>{pItem.VF || pItem.VisitFq || "-"}</td>
                          <td className="text-end">
                            <Button
                              color="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => handleDeletePlannedItem(tpId)}
                            >
                              <i className="icofont icofont-trash" style={{ fontSize: "16px" }} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Bottom Sticky Action Bar */}
      <div className="w-100">
        <Button
          color="primary"
          block
          size="lg"
          disabled={loading || (activity !== "NCA" && selectedCustomerIds.length === 0)}
          onClick={handleAddToPlan}
          className="font-weight-bold py-3 shadow"
          style={{ borderRadius: "8px" }}
        >
          {loading ? "Adding to Plan..." : "Add to plan"}
        </Button>
      </div>
    </div>
  );
};

export default MtpDayPlanner;
